import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errText } from "../lib/api";
import { liveStore } from "../lib/liveStore";
import { Avatar, Loading, Empty, SectionHead } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Radio, Camera, MonitorUp, Gamepad2 } from "lucide-react";
import { toast } from "sonner";

export default function Live() {
  const [streams, setStreams] = useState(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [starting, setStarting] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get("/live").then(({ data }) => setStreams(data)).catch(() => setStreams([])); }, []);

  const start = async (source) => {
    if (!title.trim()) return toast.error("Give your stream a title");
    setStarting(true);
    try {
      let stream = null;
      if (source === "camera") stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      else stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      const { data } = await api.post("/live", { title, source });
      liveStore.stream = stream; liveStore.liveId = data.id;
      nav(`/live/${data.id}`);
    } catch (e) {
      if (e?.name === "NotAllowedError") toast.error("Permission denied for camera/screen.");
      else toast.error(errText(e));
      setStarting(false);
    }
  };

  return (
    <div className="px-4 md:px-6 py-5">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display font-bold text-2xl">VStar Live</h1><p className="text-sm text-muted-foreground">Broadcast your virtual life. Earn live tips.</p></div>
        <Button onClick={() => setOpen(true)} className="btn-gold rounded-full h-11 px-6" data-testid="go-live-btn"><Radio size={18} className="mr-1.5" /> Go Live</Button>
      </div>

      {streams === null ? <Loading /> : streams.length === 0 ? <Empty icon={Radio} title="No one is live right now" hint="Be the first to go live and your avatar becomes the show." /> : (
        <><SectionHead title="Live Now" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {streams.map((ls) => (
              <Link key={ls.id} to={`/live/${ls.id}`} className="group surface rounded-xl overflow-hidden card-hover" data-testid={`live-${ls.id}`}>
                <div className="relative aspect-video bg-gradient-to-br from-[#131826] to-black flex items-center justify-center">
                  <Avatar src={ls.creator?.avatar_url} name={ls.creator?.display_name} size={64} ring />
                  <span className="absolute top-2 left-2 flex items-center gap-1 text-xs font-bold bg-destructive px-2 py-0.5 rounded"><span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> LIVE</span>
                  <span className="absolute bottom-2 right-2 text-xs glass px-2 py-0.5 rounded">{ls.viewer_count} watching</span>
                </div>
                <div className="p-3"><div className="font-medium text-sm line-clamp-1">{ls.title}</div><div className="text-xs text-muted-foreground mt-0.5">{ls.creator?.display_name}</div></div>
              </Link>
            ))}
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="glass border-white/10 sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Go Live</DialogTitle></DialogHeader>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Stream title" className="surface-2 border-white/10" data-testid="live-title-input" />
          <p className="text-xs text-muted-foreground">Choose your broadcast source. Your camera/screen is captured in-browser for your live preview.</p>
          <div className="grid grid-cols-3 gap-2">
            {[["camera", Camera, "Camera"], ["screen", MonitorUp, "Screen"], ["screen", Gamepad2, "Game"]].map(([s, Icon, label], i) => (
              <button key={i} disabled={starting} onClick={() => start(s)} data-testid={`live-source-${label.toLowerCase()}`} className="surface-2 border border-white/10 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-eblue/50 transition-colors disabled:opacity-50">
                <Icon size={22} className="text-eblue" /><span className="text-xs">{label}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
